import { useEffect, useRef, useState } from 'react'
import { OrderItem } from 'mytours-read-api-typescript'
import { getDirectApi } from 'mytours-read-common'

import { ionHistory } from 'mobile_web/modules/navigation'

export interface BundlePurchaseReturn {
	bundleId: number
	accountId?: number
	/** The order is ours but the code has not arrived — show a spinner while we poll. */
	purchaseValidating?: boolean
	purchasedCode?: string
	purchasedEmail?: string
	/** The code never arrived; it is also emailed, so offer manual entry instead. */
	purchaseTimedOut?: boolean
}

export const POLL_ATTEMPTS = 3

/**
 * Picks up a Stripe redirect that bought a bundle.
 *
 * PayModal sets `return_url` to whatever page the buyer was on, so the redirect lands back wherever the
 * purchase started — a screen's link group, or a collection's lock gate. Whichever component is showing
 * has to notice the `order-id`, work out whether the order is one of its bundles, and wait for the code
 * the webhook mints, which is usually not there yet when the browser returns.
 *
 * Callers say which bundles are theirs, and an order for a bundle none of them names is left alone.
 * Note the test is membership, not ownership: two mounted components whose lists overlap will both
 * pick up the same order.
 */
export function useBundlePurchaseReturn({
	bundleIds,
	accountIdFor,
	enabled = true,
}: {
	bundleIds: number[]
	accountIdFor?: (bundleId: number) => number | undefined
	enabled?: boolean
}): { purchase: BundlePurchaseReturn | null; dismiss: () => void } {
	const [purchase, setPurchase] = useState<BundlePurchaseReturn | null>(null)
	// Set when the user closes the dialog, so a late poll result cannot resurrect a modal they dismissed.
	const dismissed = useRef(false)
	// The effect keys off the ids, so hold them in a ref to keep a changing array identity from re-running it.
	const bundleIdsRef = useRef(bundleIds)
	bundleIdsRef.current = bundleIds
	const accountIdForRef = useRef(accountIdFor)
	accountIdForRef.current = accountIdFor

	const key = bundleIds.join(',')

	useEffect(() => {
		if (!enabled || !key) {
			return
		}

		const url = new URL(window.location.href)
		const orderId = Number(url.searchParams.get('order-id'))
		if (!orderId) {
			return
		}
		const paymentIntent = url.searchParams.get('payment_intent')

		const stripStripeParams = (): void => {
			const params = new URL(window.location.href).searchParams
			params.delete('order-id')
			params.delete('payment_intent')
			params.delete('payment_intent_client_secret')
			params.delete('redirect_status')
			const query = params.toString()
			ionHistory.replace(window.location.pathname + (query ? '?' + query : '') + window.location.hash)
		}

		let cancelled = false
		const aborted = (): boolean => cancelled || dismissed.current

		const resolve = async (): Promise<void> => {
			// order_items are present immediately, so the bundle is identifiable before the code exists.
			let order
			try {
				order = await getDirectApi().orderShow(orderId, paymentIntent)
			} catch {
				return
			}
			if (aborted()) {
				return
			}

			const item = order.items?.[0]
			if (item?.item_type !== OrderItem.ItemTypeEnum.Bundle || !item.item_id) {
				return
			}
			const bundleId = item.item_id
			if (!bundleIdsRef.current.includes(bundleId)) {
				return
			}

			// Ours. Drop the Stripe params so a re-render cannot reprocess the same redirect, leaving
			// every other param and the fragment alone. Passed as a string: ionHistory takes a
			// history@4 location, and a URL instance spreads to {} there, blanking search and hash.
			stripStripeParams()

			const base = { bundleId, accountId: accountIdForRef.current?.(bundleId) }
			if (item.code) {
				setPurchase({ ...base, purchasedCode: item.code, purchasedEmail: order.email })
				return
			}

			setPurchase({ ...base, purchaseValidating: true })

			for (let retry = 0; retry < POLL_ATTEMPTS && !aborted(); retry++) {
				await new Promise(done => setTimeout(done, 2000 * (retry + 1)))
				if (aborted()) {
					return
				}
				let next
				try {
					next = await getDirectApi().orderShow(orderId, paymentIntent)
				} catch {
					next = undefined
				}
				if (aborted()) {
					return
				}
				const code = next?.items?.[0]?.code
				if (code) {
					setPurchase({ ...base, purchasedCode: code, purchasedEmail: next.email })
					return
				}
			}

			if (!aborted()) {
				setPurchase({ ...base, purchaseTimedOut: true })
			}
		}

		void resolve()

		return () => {
			cancelled = true
		}
	}, [enabled, key])

	return {
		purchase,
		dismiss: () => {
			dismissed.current = true
			setPurchase(null)
		},
	}
}
